import { cn } from "@/lib/utils";

export type RadarAxis = { label: string; value: number }; // value 0..100

/**
 * Hand-drawn feeling polygon chart of a person's civic footprint.
 * Deliberately plain geometry — no glow, no gradients-for-the-sake-of-it.
 */
export function ImpactRadar({ axes, className }: { axes: RadarAxis[]; className?: string }) {
  const size = 220;
  const c = size / 2;
  const r = c - 34;
  const n = Math.max(3, axes.length);

  const point = (i: number, ratio: number) => {
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
    return [c + Math.cos(angle) * r * ratio, c + Math.sin(angle) * r * ratio] as const;
  };

  const rings = [0.25, 0.5, 0.75, 1];
  const shape = axes
    .map((a, i) => point(i, Math.max(0.06, Math.min(1, a.value / 100))))
    .map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`)
    .join(" ");

  return (
    <div className={cn("flex justify-center", className)}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Impact footprint">
        {rings.map((ring) => (
          <polygon
            key={ring}
            points={axes.map((_, i) => point(i, ring).map((v) => v.toFixed(1)).join(",")).join(" ")}
            fill="none"
            stroke="var(--border)"
            strokeWidth={1}
          />
        ))}
        {axes.map((_, i) => {
          const [x, y] = point(i, 1);
          return <line key={i} x1={c} y1={c} x2={x} y2={y} stroke="var(--border)" strokeWidth={1} />;
        })}
        <polygon points={shape} fill="color-mix(in oklab, var(--foreground) 14%, transparent)" stroke="var(--foreground)" strokeWidth={2} strokeLinejoin="round" />
        {axes.map((a, i) => {
          const [x, y] = point(i, Math.max(0.06, Math.min(1, a.value / 100)));
          return <circle key={a.label} cx={x} cy={y} r={3.5} fill="var(--foreground)" />;
        })}
        {axes.map((a, i) => {
          const [x, y] = point(i, 1.22);
          return (
            <text
              key={a.label}
              x={x}
              y={y}
              textAnchor={x > c + 4 ? "start" : x < c - 4 ? "end" : "middle"}
              dominantBaseline="middle"
              fontSize="10"
              fill="var(--muted-foreground)"
              fontWeight={600}
            >
              {a.label}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
